import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, switchMap } from 'rxjs';
import {
  DEV_FALLBACK_METADATA,
  parseRuntimeVersionMetadata,
  type RuntimeVersionMetadata,
} from './runtime-version.validation';

export type { RuntimeVersionMetadata } from './runtime-version.validation';

@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly http = inject(HttpClient);
  private cache$?: Observable<RuntimeVersionMetadata>;

  /** Drop cached runtime metadata so the next load fetches fresh deployment info. */
  invalidateCache(): void {
    this.cache$ = undefined;
  }

  /** Runtime deployment metadata from build-generated `/assets/version.json`. */
  load(): Observable<RuntimeVersionMetadata> {
    if (!this.cache$) {
      this.cache$ = this.fetchRuntimeMetadata().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.cache$;
  }

  private fetchRuntimeMetadata(): Observable<RuntimeVersionMetadata> {
    return this.http
      .get('/assets/deployment-id.txt', { responseType: 'text' })
      .pipe(
        switchMap((deploymentId) => {
          const build = encodeURIComponent(deploymentId.trim());
          return this.http.get<unknown>(`/assets/version.json?build=${build}`);
        }),
        map((raw) => this.coerceMetadata(raw)),
        catchError((err) => {
          console.warn('[VersionService] Failed to load runtime metadata', err);
          return of({ ...DEV_FALLBACK_METADATA });
        })
      );
  }

  private coerceMetadata(raw: unknown): RuntimeVersionMetadata {
    const parsed = parseRuntimeVersionMetadata(raw);
    if (parsed) {
      return parsed;
    }
    console.warn(
      '[VersionService] Invalid runtime metadata schema; using development fallback'
    );
    return { ...DEV_FALLBACK_METADATA };
  }
}
